import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { type Language } from './LandingPage'

const content = {
  pt: {
    title: 'Perguntas frequentes',
    faqs: [
      { question: 'PinDay é agenda de horários ou reserva de datas?', answer: 'Depende da configuração do serviço. PinDay confirma a reserva com pagamento, regras e o bloqueio correto no calendário.' },
      { question: 'Qual a diferença entre one-off e recorrente?', answer: 'One-off é cobrança única com sinal e saldo posterior. Recorrente é assinatura via Stripe para Pro e Studio.' },
      { question: 'Posso cobrar sinal?', answer: 'Sim. No fluxo one-off você pode cobrar sinal e depois enviar saldo quando aplicável.' },
      { question: 'Como funciona recorrente?', answer: 'O cliente ativa uma assinatura no checkout Stripe. Esse fluxo não usa sinal e não suporta add-ons hoje.' },
      { question: 'Preciso do Stripe para recorrente?', answer: 'Sim. O fluxo recorrente depende de Stripe e Stripe Connect.' },
      { question: 'Como funciona a fila de espera?', answer: 'Em Pro e Studio, a fila é por data. Quando a vaga é liberada dentro do PinDay, a próxima pessoa recebe uma oferta exclusiva de 24h por e-mail.' },
      { question: 'A fila de espera é automática no WhatsApp?', answer: 'Não. O MVP envia e-mail automático e guarda telefone para follow-up manual do prestador por WhatsApp.' },
      { question: 'O que acontece no calendário quando o pagamento confirma?', answer: 'O bloqueio configurado é aplicado e pode ser sincronizado com Google Calendar, com fallback ICS.' },
      { question: 'Como funcionam cancelamento e reembolso?', answer: 'A política define retenção, reembolso e reagendamento. O cálculo segue a regra configurada.' },
    ],
  },
  en: {
    title: 'Frequently asked questions',
    faqs: [
      { question: 'Is PinDay for time slots or date reservations?', answer: 'It depends on the service configuration. PinDay confirms the booking with payment, rules, and the right calendar block.' },
      { question: 'What is the difference between one-off and recurring?', answer: 'One-off is a single charge with deposit and later balance. Recurring is a Stripe subscription for Pro and Studio.' },
      { question: 'Can I charge a deposit?', answer: 'Yes. In the one-off flow you can collect a deposit and send the remaining balance later when needed.' },
      { question: 'How does recurring work?', answer: 'The client activates a subscription through Stripe checkout. This flow has no deposit and no add-ons today.' },
      { question: 'Do I need Stripe for recurring?', answer: 'Yes. The recurring flow depends on Stripe and Stripe Connect.' },
      { question: 'How does the waitlist work?', answer: 'On Pro and Studio, the queue is per date. When a slot is released inside PinDay, the next person gets an exclusive 24-hour offer by email.' },
      { question: 'Is the waitlist automatic on WhatsApp?', answer: 'No. The MVP sends automatic email and keeps the phone number for manual WhatsApp follow-up by the provider.' },
      { question: 'What happens in the calendar after payment confirms?', answer: 'The configured block is applied and can sync to Google Calendar, with ICS fallback.' },
      { question: 'How do cancellations and refunds work?', answer: 'The policy defines retention, refund, and reschedule rules. The calculation follows the configured rule.' },
    ],
  },
  es: {
    title: 'Preguntas frecuentes',
    faqs: [
      { question: '¿PinDay es para horarios o para reservar fechas?', answer: 'Depende de la configuración del servicio. PinDay confirma la reserva con pago, reglas y el bloqueo correcto en el calendario.' },
      { question: '¿Cuál es la diferencia entre one-off y recurrente?', answer: 'One-off es un cobro único con seña y saldo posterior. Recurrente es una suscripción Stripe para Pro y Studio.' },
      { question: '¿Puedo cobrar seña?', answer: 'Sí. En el flujo one-off puedes cobrar seña y luego enviar el saldo cuando haga falta.' },
      { question: '¿Cómo funciona recurrente?', answer: 'El cliente activa una suscripción en el checkout de Stripe. Ese flujo no usa seña ni add-ons hoy.' },
      { question: '¿Necesito Stripe para recurrente?', answer: 'Sí. El flujo recurrente depende de Stripe y Stripe Connect.' },
      { question: '¿Cómo funciona la lista de espera?', answer: 'En Pro y Studio, la fila es por fecha. Cuando la fecha se libera dentro de PinDay, la siguiente persona recibe una oferta exclusiva de 24 horas por email.' },
      { question: '¿La lista de espera es automática por WhatsApp?', answer: 'No. El MVP envía email automático y guarda el teléfono para seguimiento manual por WhatsApp.' },
      { question: '¿Qué pasa en el calendario cuando el pago se confirma?', answer: 'El bloqueo configurado se aplica y puede sincronizarse con Google Calendar, con fallback ICS.' },
      { question: '¿Cómo funcionan cancelación y reembolso?', answer: 'La política define retención, reembolso y reagendamiento. El cálculo sigue la regla configurada.' },
    ],
  },
}

interface FAQProps {
  language: Language
}

export function FAQ({ language }: FAQProps) {
  const t = content[language]

  return (
    <section id="faq" className="bg-[#fffaf3] px-6 py-20">
      <div className="mx-auto max-w-4xl">
        <h2 className="text-center text-4xl font-semibold tracking-tight text-[#0b2a4a] sm:text-5xl">{t.title}</h2>
        <Accordion type="single" collapsible className="mt-10 space-y-4">
          {t.faqs.map((faq, index) => (
            <AccordionItem key={faq.question} value={`faq-${index}`} className="rounded-[24px] border border-[#0b2a4a]/10 bg-white px-6">
              <AccordionTrigger className="text-left text-lg font-semibold text-[#0b2a4a] hover:no-underline">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="pb-6 text-base leading-7 text-[#0b2a4a]/72">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  )
}
